import { ArrowUpRight, ArrowDown, GraduationCap, BookOpen, Mail } from 'lucide-react';
import { profile, journey, research, udemyActivity } from '../profileData';

export function ProfileSection() {
  return (
    <section id="about" className="about-section">
      <div className="section">
        <div className="section-heading about-heading about-with-portrait">
          <div className="about-summary">
            <p className="eyebrow section-index">06 / PROFILE & DIRECTION</p>
            <h2>現場の声を、<br /><span>チームで形にできる人へ</span></h2>
            <div className="about-intro"><p>{profile.value}</p></div>
          </div>
          <figure className="profile-portrait">
            <picture>
              <source type="image/webp" srcSet={profile.portrait.srcSet} sizes="(max-width: 680px) 166px, 222px" />
              <img src={profile.portrait.src} alt={profile.portrait.alt} width={profile.portrait.width} height={profile.portrait.height} loading="lazy" decoding="async" />
            </picture>
            <figcaption><span>{profile.name}<small>{profile.englishName}</small></span><ArrowUpRight size={24} strokeWidth={1.3} aria-hidden="true" /></figcaption>
          </figure>
        </div>
        <div className="profile-facts">
          <div className="academic-profile">
            <p className="eyebrow">PROFILE</p>
            <h3>{profile.name}<span>{profile.englishName}</span></h3>
            <p className="academic-institution">{profile.institution}</p>
            <p className="academic-major">{profile.school}<br />{profile.major}<br />{profile.degree}</p>
            <span className="graduation-label">{profile.graduation}</span>
          </div>
          <div className="career-direction">
            <p className="eyebrow">MY DIRECTION</p>
            <h3>エンジニアから、<br />現場とチームをつなぐPMへ</h3>
            <p>{profile.ambition}</p>
            <div className="interest-tags" aria-label="関心のある技術領域">{profile.interests.map(interest => <span key={interest}>{interest}</span>)}</div>
          </div>
        </div>
        <div className="qualification-strip">
          <div><GraduationCap size={24} strokeWidth={1.5} /><h3>教員免許<span>取得済み</span></h3></div>
          <ul>{profile.licenses.map(license => <li key={license}>{license}</li>)}</ul>
        </div>
      </div>
    </section>
  );
}

export function UdemyActivity() {
  return <section id="udemy" className="udemy-activity" aria-labelledby="udemy-title">
    <div className="udemy-activity-heading"><p className="eyebrow">LEARNING & SHARING</p><span><BookOpen size={18} aria-hidden="true" /> Udemy講座制作</span></div>
    <div className="udemy-activity-content">
      <div className="udemy-activity-copy">
        <p className="udemy-activity-label">力を入れてきたこと</p>
        <h3 id="udemy-title">{udemyActivity.title.map(part => <span key={part}>{part}</span>)}</h3>
        {udemyActivity.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
        <a href={profile.udemy} target="_blank" rel="noreferrer" className="udemy-profile-link">Udemyで講座を見る <ArrowUpRight size={18} aria-hidden="true" /></a>
      </div>
      <div className="udemy-activity-results">
        <dl className="udemy-metrics">{udemyActivity.metrics.map(metric => <div key={metric.label}><dt>{metric.label}</dt><dd><strong>{metric.value}</strong><span>{metric.unit}</span></dd></div>)}</dl>
        <p className="udemy-as-of"><time dateTime={udemyActivity.asOf}>{udemyActivity.asOfLabel}</time></p>
        <p className="udemy-learning-note">学ぶ。整理する。わかりやすく伝える。<br />その繰り返しを、学び続ける習慣に。</p>
      </div>
    </div>
  </section>;
}

export function ResearchMetrics() {
  return <div className="research-metrics">{research.metrics.map(metric => <div className="research-metric" key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span><small>{metric.comparison}</small></div>)}</div>;
}

function ResearchVideo() {
  return <figure className="research-video">
    <video key={research.video.src} controls playsInline preload="metadata" poster={research.video.poster} width="1280" height="720" aria-label="電気柵見回りロボットの動作動画">
      <source src={research.video.webm} type='video/webm; codecs="vp8, vorbis"' />
      <source src={research.video.src} type='video/mp4; codecs="avc1.42c01f, mp4a.40.2"' />
      お使いのブラウザーでは動画を再生できません。<a href={research.video.src}>動作動画を開く</a>
    </video>
    <figcaption>{research.video.caption}</figcaption>
  </figure>;
}

export function ResearchSection({ onOpen }) {
  return <section id="research" className="research-section section">
    <div className="research-label"><span className="eyebrow">03 / FIELD RESEARCH</span><span className="research-status">AI / ROBOTICS</span></div>
    <div className="research-main"><div><h2>画面の中で動く<br /><span>その先まで</span></h2><p>現場で確かめ、実際に使えるものへ。</p></div><button onClick={onOpen} className="research-link" aria-label="研究について見る"><ArrowUpRight size={42} strokeWidth={1.3} /></button></div>
    <p className="research-story-intro">地域の方から直接課題を聞き、コスト面も考慮しながら、電気柵見回りロボットの研究へ。何度も山に入り、実環境で実験とデータ収集を重ね、有用性を評価しました。</p>
    <div className="research-feature">
      <ResearchVideo />
      <div className="research-feature-copy"><p className="eyebrow">GRADUATION RESEARCH</p><h3>{research.title}</h3><p>{research.overview}</p><div className="research-video-steps" aria-label="ロボットの処理の流れ"><span>移動</span><span>停止・撮影</span><span>異常検出</span></div><button className="text-button" onClick={onOpen}>研究の詳細を見る <ArrowUpRight size={18} /></button></div>
    </div>
    <div className="field-challenge"><span className="eyebrow">TRIAL, ERROR, AND THE FIELD</span><h3>模擬環境では動いた<br />実環境では、うまくいかなかった</h3><p>{research.challenge}</p></div>
    <ResearchMetrics />
    <p className="research-conditions">{research.conditions}</p>
    <p className="research-results-note">{research.tradeoff}</p>
    <ResearchPublications />
  </section>;
}

function ResearchPublications() {
  return <div className="publication-list" aria-label="論文・学会発表"><p className="eyebrow">PRESENTATION & THESIS</p>{research.publications.map(publication => <article className="publication" key={publication.type}><div className="publication-type"><span>{publication.type}</span>{publication.location && <small>{publication.location}</small>}</div><div><h3>{publication.title}</h3><p>{publication.authors}</p><p>{publication.note}</p><a className="publication-pdf-link" href={publication.pdf} target="_blank" rel="noreferrer">論文を読む <span>PDF・{publication.pages}ページ</span><ArrowUpRight size={17} aria-hidden="true" /></a></div></article>)}</div>;
}

export function ResearchDetails() {
  return <>
    <p className="eyebrow">AI / ROBOTICS — FIELD RESEARCH</p><h2 id="dialog-title">{research.title}</h2><p className="research-scope">{research.label}</p><p className="dialog-intro">{research.overview}</p>
    <div className="research-detail-block"><h3>研究で取り組んだこと</h3><p>{research.contribution}</p></div>
    <ol className="research-methods">{research.methods.map((method, index) => <li key={method.title}><span>0{index + 1}</span><div><h3>{method.title}</h3><p>{method.text}</p></div></li>)}</ol>
    <figure className="research-method-figure"><img src={research.methodImage} alt="電気柵の支柱間距離、撮影幅、重複率から撮影地点を決める提案手法の図" width="1709" height="804" loading="lazy" /><figcaption>卒業論文 図4：電気柵・経由点・撮影地点の関係</figcaption></figure>
    <ResearchMetrics /><p className="research-conditions">{research.conditions}</p><p className="research-results-note">{research.tradeoff}</p>
    <div className="research-detail-block"><h3>現地での検証</h3><p>{research.challenge}</p></div>
    <figure className="research-field"><img src={research.fieldImage} alt="実地実験で用いた電気柵の巡回経路。航空写真上に経由点、始点、終点、基準局を表示。" width="1920" height="1080" loading="lazy" /><figcaption>卒業論文 図6：福島県会津美里町での実験経路</figcaption></figure>
    <div className="research-detail-block research-video-detail"><h3>実際のロボットの動き</h3><p>{research.video.description}</p><ResearchVideo /></div>
    <div className="research-detail-block"><h3>使用技術・実験環境</h3><div className="project-tags">{research.technologies.map(technology => <span key={technology}>{technology}</span>)}</div><p className="research-model-note">{research.modelNote}</p></div>
    <ResearchPublications />
    <p className="research-source-note">出典：卒業論文の図4・図6、表2・表5・表7、および情報処理学会発表原稿の表1・表2。</p>
  </>;
}

export function JourneySection() {
  return <section id="journey" className="section journey-section">
    <div className="journey-intro"><p className="eyebrow section-index">05 / EXPERIENCE & INTERNSHIPS</p><h2>Always<br /> in motion</h2><p>ソフトウェアから制御、AIの活用へ。<br />現場で学び、実装する経験を重ねています。</p><ArrowDown size={31} strokeWidth={1.3} /></div>
    <div className="journey-list">{journey.map(item => <article className={'experience-row ' + (item.current ? 'is-current' : '') + (item.upcoming ? ' is-upcoming' : '')} key={item.id}>
      <div className="experience-top"><span className="experience-date">{item.date}{item.duration && <span> / {item.duration}</span>}</span><span className="experience-status">{item.current && <i className="status-dot" />}{item.status}</span></div>
      <h3>{item.title}</h3><p className="experience-role">{item.role}</p><p className="experience-overview">{item.detail}</p>
      {item.highlights.length > 0 && <ul className="experience-highlights">{item.highlights.map(highlight => <li key={highlight}>{highlight}</li>)}</ul>}
      {item.photos && <div className="experience-photos">{item.photos.map(photo => <figure key={photo.src}><img src={photo.src} width={photo.width} height={photo.height} alt={photo.alt} loading="lazy" decoding="async" /><figcaption>{photo.caption}</figcaption></figure>)}</div>}
      {(item.tags.length > 0 || item.url) && <div className="experience-footer"><div className="project-tags">{item.tags.map(tag => <span key={tag}>{tag}</span>)}</div>{item.url && <a href={item.url} target="_blank" rel="noreferrer" aria-label={item.title + 'の会社サイトを開く'}>会社サイト <ArrowUpRight size={15} /></a>}</div>}
    </article>)}</div>
  </section>;
}

export function ContactLinks({ inDialog = false }) {
  return <div className={'profile-contact-links ' + (inDialog ? 'in-dialog' : '')}>
    <a className="email-link" href={'mailto:' + profile.email}><Mail size={18} /><span>{profile.email}</span></a>
    <div className="social-profile-links"><a href={profile.github} target="_blank" rel="noreferrer">GitHub <ArrowUpRight size={15} /></a><a href={profile.udemy} target="_blank" rel="noreferrer">Udemy <ArrowUpRight size={15} /></a></div>
  </div>;
}

